import { Separator } from "@/components/ui/separator";
import useSWR from "swr";
import { Loader } from "lucide-react";
import { LoadingError } from "@/components/LoadingError";
import { TableType, getAll } from "@/lib/supabase";
import { useAppStore } from "@/stores";
import { PlanBasic } from "./steps/1-basic";
import { DealSelector } from "./steps/2-deal-selector";
import { Rates } from "./steps/3-rates";
import { Stepper, Step, useStepper } from "@/components/ui/stepper";

export function BuildPlanPage() {
  const currentUser = useAppStore((state) => state.currentUser);

  const {
    data: tokens,
    error: tokensError,
    isLoading: tokensLoading,
  } = useSWR(
    [
      "tokens" as TableType,
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([table, companyId]) => getAll(table, companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  if (tokensLoading) return <Loader />;
  if (tokensError) return <LoadingError />;

  return (
    <Stepper>
      <StepperHeader />
      <Separator className="my-6" />
      <Step step={1}>
        <PlanBasic />
      </Step>
      <Step step={2}>
        <DealSelector tokens={tokens} />
      </Step>
      <Step step={3}>
        <Rates />
      </Step>
    </Stepper>
  );
};

function getPlanText(step: number) {
  if (step == 1) return ({
    title: "Basics",
    subtitle: "Configure the basics of your compensation plan",
  });

  if (step == 2) return ({
    title: "Deals",
    subtitle: "Select which deals apply to this plan",
  });

  if (step == 3) return ({
    title: "Rates",
    subtitle: "Configure the rates which apply to this plan",
  });

  return ({
    title: "Setup",
    subtitle: "Configure the details of your compensation plan",
  });
}

function StepperHeader() {
  const { currentStep } = useStepper();
  const planText = getPlanText(currentStep);

  return (
    <div className="space-y-0.5">
      <h2 className="text-2xl font-bold tracking-tight">
        New plan: {planText.title}
      </h2>
      <p className="text-muted-foreground">
        {planText.subtitle}
      </p>
    </div>
  );
}
